import { EvaEndpoint } from './endpoint';
import { IEvaServiceDefinition } from '@springtree/eva-services-core';
import { evaServiceInterceptors } from './interceptors';
import { getServiceSettings } from './settings';
import { logger } from '@springtree/eva-sdk-core-logger';
import { getCoreSettings } from '@springtree/eva-sdk-core-settings';
import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios';
import { v4 } from 'uuid';

/**
 * Constructor arguments for EVA service
 *
 * @export
 * @interface IEvaServiceArguments
 */
export interface IEvaServiceArguments {
  /**
   * Optional flag to disable axios interceptors
   * This is an advanced feature that will more then likely never be set
   * The EVA bootstrap requires this option for pre-bootstrap service calls
   *
   * @type {boolean}
   */
  disableInterceptors?: boolean;

  /**
   * Disables the endpoint is bootstrapped check
   * Needed for the endpoint itself to do the bootstrap calls
   *
   * @type {boolean}
   */
  disableEndpointBootstrapCheck?: boolean;
}

/**
 * The additional options to use for an EVA Service call
 * These can be used to override any defaults
 *
 * @export
 * @interface IEvaServiceCallOptions
 */
export interface IEvaServiceCallOptions {

  /**
   * Used to override the default service timeout
   *
   * @type {number}
   */
  timeout?: number;

  /**
   * A specific authentication token to use for this service call
   * Otherwise the endpoints default token will be used
   *
   * @type {string}
   */
  authenticationToken?: string;

  /**
   * Set this flag to disable the default behaviour of setting the endpoint
   * default token if no `authenticationToken` is supplied.
   * This will also disable the current user token from the Redux store from being set
   *
   * @type {boolean}
   */
  omitAuthenticationToken?: boolean;

  /**
   * Explicitly requested sidekick mode
   * Will default to whatever the on premise module status dictates
   *
   * @type {('false'| 'true' |'requested')}
   */
  sidekickMode?: 'false'| 'true' |'requested';

  /**
   * Each service call is provided with a unique context.
   * You can provide a value if you do not want an auto-generated one
   *
   * @type {string}
   */
  serviceContext?: string;

  /**
   * If an EVA Service is called in a chain this identifier can be added to
   * to the headers to identify the collection of services
   *
   * @type {string}
   */
  chainContext?: string;

  /**
   * Can be used by clients to set the current station ID header value
   * This should always reflect the ID from the currently used station from
   * the `ListStationsForOrganization` response. Added for legal reasons
   * Is usually set using the service settings but can be overridden per call
   * using this setting
   *
   * @type {(number|undefined)}
   */
  currentStationID?: number|undefined;
}

/**
 * A service error response for the EVA backend
 *
 * @export
 * @interface IEvaServiceError
 */
export interface IEvaServiceError {
  status: number;
  data: any;
}

/**
 * The EVA Service implementation
 *
 * @export
 * @class EvaService
 * @template REQ
 * @template RES
 */
export class EvaService<SVC extends IEvaServiceDefinition> {

  /**
   * The Axios instance used to make the call to the EVA Backend
   *
   * @private
   * @type {AxiosInstance}
   */
  private instance: AxiosInstance;

  /**
   * The name of the EVA Service being called
   *
   * @private
   */
  private serviceName = 'generic';

  /**
   * The full Axios response
   *
   * @type {(AxiosResponse<unknown>|undefined)}
   */
  public axiosResponse: AxiosResponse<unknown>|undefined;

  /**
   * Creates an instance of EvaService which creates an Axios instance
   * pre-configured with default EVA headers and options
   *
   * @param {SVC} data The service definition that will be used to make the call and receive the result
   * @param {EvaEndpoint} endpoint The bootstrapped EVA endpoint to use for the call
   * @param {IEvaServiceArguments} [args] Optional arguments for instantiating the service call
   */
  constructor(
    public data: SVC,
    public readonly endpoint: EvaEndpoint,
    args?: IEvaServiceArguments,
  ) {
    if (!this.data || !this.data.path) {
      throw new Error('Missing required EVA service url');
    }

    if (!this.endpoint || !this.endpoint.hasBootstrapped && (!args || !args.disableEndpointBootstrapCheck)) {
      throw new Error('Endpoint has not been bootstrapped');
    }

    // Extract the service name from the path
    //
    const urlSplit = `${this.data.path}`.split('/');
    this.serviceName = urlSplit[urlSplit.length - 1];

    // Build the request url by combining the service path with the endpoint
    //
    const requestUrl = `${this.endpoint.endPointUri}${this.data.path}`;

    // Collect required settings
    //
    const coreSettings = getCoreSettings();
    const serviceSettings = getServiceSettings();

    // Build the request options setting default headers
    // and parameters
    //
    const defaultOptions: AxiosRequestConfig = {
      url:                  requestUrl,
      method:               'POST',
      timeout:              serviceSettings.defaultTimeout,
      responseType:         'json',
      headers: {
        Accept:             'application/json',
        'Accept-Language':  coreSettings.language,
        'Content-type':     'application/json',
        ClientName:         coreSettings.clientName,
        ClientVersion:      coreSettings.clientVersion,
        'EVA-User-Agent':   `${coreSettings.appName}/${coreSettings.appVersion}`,
        'EVA-SidekickMode': 'false',
      },
    };

    logger.debug(`[EVA:${this.serviceName}] Creating instance`, defaultOptions);
    this.instance = axios.create(defaultOptions);

    // Add currently active interceptors
    //
    if (!args || !args.disableInterceptors) {
      evaServiceInterceptors.getActiveInterceptors().forEach((interceptor) => {
        if (interceptor.requestInterceptor) {
          this.instance.interceptors.request.use(interceptor.requestInterceptor.success, interceptor.requestInterceptor.success);
        }
        if (interceptor.responseInterceptor) {
          this.instance.interceptors.response.use(interceptor.responseInterceptor.success, interceptor.responseInterceptor.success);
        }
      });
    }
  }

  /**
   * Sets the request payload
   *
   * @param {Pick<Required<SVC>, 'request'>} request
   */
  public setRequest(request: Pick<SVC, 'request'>) {
    this.data.request = request;
  }

  /**
   * Call the EVA backend service with the current value of `this.data.request`
   * as the payload. The response will be set on `this.data.response` and the
   * full Axios response will be available on `this.axiosResponse`.
   * Unless otherwise configured in the construcor all active interceptors will
   * be used on the request and response as required.
   *
   * @returns {Promise<RES>}
   */
  public async call(options?: IEvaServiceCallOptions): Promise<SVC> {

    // Collect required settings
    //
    const serviceSettings = getServiceSettings();

    // The Axios instance config will be amended with everything we add here
    //
    const config: AxiosRequestConfig = {
      ...this.instance.defaults,
      data: this.data.request,
    };

    // Override timeout if requested
    // Don't use falsy check to allow 0 values
    //
    if (options && options.timeout !== undefined) {
      config.timeout = options.timeout;
    }

    // Set custom sidekick mode if specified
    //
    if (options && options.sidekickMode) {
      config.headers['EVA-SidekickMode'] = options.sidekickMode;
    }

    // Add authentication token if supplied
    //
    if (options && options.authenticationToken) {
      config.headers.Authorization = options.authenticationToken;
    } else if (!options || !options.omitAuthenticationToken) {
      const defaultToken = this.endpoint.getDefaultToken();
      if (defaultToken) {
        config.headers.Authorization = defaultToken;
      }
    }

    // Add a unique call identifier header which will be echoed by the EVA backend
    //
    config.headers['EVA-App-ContextID'] = (options && options.serviceContext) || v4();
    config.headers['EVA-Service-Name'] = this.data.name;

    // Add chain context if provided
    //
    if (options && options.chainContext) {
      config.headers['EVA-App-ContextChainID'] = options.chainContext;
    }

    // Add the currently used station ID if set
    //
    if (options && options.currentStationID) {
      config.headers['EVA-StationID'] = `${options.currentStationID}`;
    } else if (serviceSettings.currentStationID) {
      config.headers['EVA-StationID'] = `${serviceSettings.currentStationID}`;
    }

    logger.debug(`[EVA:${this.serviceName}] Doing call`, config);

    try {
      const axiosResponse: AxiosResponse<unknown> = await this.instance.request(config);
      logger.debug(`[EVA:${this.serviceName}] Call response status`, axiosResponse.status);
      logger.debug(`[EVA:${this.serviceName}] Call response data`, axiosResponse.data);
      logger.debug(`[EVA:${this.serviceName}] Call response headers`, axiosResponse.headers);

      // Store response object and raw response
      //
      this.data.response = axiosResponse.data;
      this.axiosResponse = axiosResponse;

      // Resolve with typed response object
      //
      return this.data;
    } catch (error) {
      // Check if this is an Axios response error
      //
      if (error.response) {
        logger.error(`[EVA:${this.serviceName}] Call failed ${error.response.status}`, error.response.data);
        const serviceError: IEvaServiceError = {
          status: error.response.status,
          data: error.response.data,
        };
        throw serviceError;
      }

      // Regular or unexpected error
      //
      logger.error(`[EVA:${this.serviceName}] Call failed`, error);
      throw error;
    }
  }
}
